<template>
  <form
    novalidate
    class="md-layout"
    @submit.prevent="validateStatsForm">
    <md-card class="md-layout-item md-small-size-100">
      <md-card-header>
        <div class="md-title">Dashboard</div>
      </md-card-header>
      <md-card-content>
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('surveyType')">
              <label for="surveyType">Survey Type</label>
              <md-select
                name="surveyType"
                id="surveyType"
                v-model="form.surveyType"
                md-dense
                :disabled="sending">
                <md-option></md-option>
                <md-option
                  v-for='opt in getSurveyTypes()'
                  :key="opt[0]"
                  :value='opt[0]'>
                    {{opt[1]}}
                </md-option>
              </md-select>
              <span class="md-error">The Survey Type is required</span>
            </md-field>
          </div>

          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('locale')">
              <label for="first-name">Locale</label>
              <md-input
                name="locale"
                id="locale"
                v-model="form.locale"
                :disabled="sending"/>
              <span class="md-helper-text">[language]_[country] e.g. fr_BE</span>
              <span
                class="md-error"
                v-if="!$v.form.locale.required">The Locale is required</span>
              <span
                class="md-error"
                v-else-if="!$v.form.locale.isLocale">
                Invalid locale (should be a valid locale like: en_GB)
              </span>
            </md-field>
          </div>
        </div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('dateFrom')">
              <md-datepicker
                name="dateFrom"
                id="dateFrom"
                v-model="form.dateFrom"
                :disabled="sending"
                md-immediately>
                  <label>Select start date</label>
              </md-datepicker>
              <span
                class="md-error"
                v-if="!$v.form.dateFrom.required">
                  The dateFrom is required
              </span>
            </md-field>
          </div>

          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('dateTo')">
              <md-datepicker
                name="dateTo"
                id="dateTo"
                v-model="form.dateTo"
                :disabled="sending"
                md-immediately>
                  <label>Select end date</label>
              </md-datepicker>
              <span
                class="md-error"
                v-if="!$v.form.dateTo.required">
                  The dateTo is required
              </span>
            </md-field>
          </div>
        </div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('groupBy')">
              <label for="groupBy">Group by</label>
              <md-select
                name="groupBy"
                id="groupBy"
                v-model="form.groupBy"
                md-dense
                :disabled="sending">
                <md-option value=""></md-option>
                <md-option
                  v-for='opt in getGroupByItems()'
                  :key="opt[0]"
                  :value='opt[0]'>
                    {{opt[1]}}
                </md-option>
              </md-select>
            </md-field>
          </div>
        </div>
      </md-card-content>

      <md-progress-bar
        md-mode="indeterminate"
        v-if="sending"/>

      <md-card-actions>
        <md-button
          type="submit"
          class="md-primary md-raised"
          :disabled="sending">
          Submit
        </md-button>
      </md-card-actions>
    </md-card>

    <md-snackbar :md-active.sync="submissionError">
      <template v-if="submissionErrorDetails">
        Error retrieving stats: {{ submissionErrorDetails.response.data.message }}
      </template>
    </md-snackbar>
  </form>
</template>

<script>

import applyConverters from 'axios-case-converter';
import { validationMixin } from 'vuelidate';
import {
  required,
  helpers,
} from 'vuelidate/lib/validators';

const isLocale = helpers.regex('isLocale', /^[a-z]{2}_[A-Z]{2}$/);

export default {
  name: 'StatsForm',
  mixins: [validationMixin],
  props: {
    value: null,
  },
  data() {
    return {
      form: {
        surveyType: null,
        locale: null,
        dateFrom: null,
        dateTo: null,
        groupBy: null,
      },
      submissionError: false,
      submissionErrorDetails: null,
      sending: false,
      result: this.value,
    };
  },

  validations: {
    form: {
      locale: {
        isLocale,
      },
      surveyType: {
        required,
      },
      dateFrom: {
      },
      dateTo: {
      },
      groupBy: {
      },
    },
  },


  methods: {
    getSurveyTypes: () => ([
      ['websurvey', 'Web Survey'],
      ['salary-survey-short', 'Web Survey Short'],
      ['salarycheck', 'Salarycheck'],
      ['f2f', 'Web Survey - Face to Face'],
      ['dwc', 'DecentWorkCheck Survey - Workers'],
      ['factory', 'DecentWorkCheck Survey - Company'],
      ['gig', 'Gig Platform Survey - Workers'],
      ['corona', 'Corona Work Life Survey - Workers'],
      ['corfact', 'Corona Work Life Survey - Company'],
      ['ets', 'Event Tracking System'],
    ]),
    getGroupByItems: () => ([
      ['locale', 'Locale'],
      ['interviewer', 'Interviewer'],
      ['region', 'Region'],
      ['industry', 'Industry'],
      ['day', 'Day'],
      ['month', 'Month'],
      ['year', 'Year'],
    ]),
    getFirstOfMonth: () => {
      const date = new Date();
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    getToday: () => new Date(),

    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];

      let result = {};
      if (field) {
        result = {
          'md-invalid': field.$invalid && field.$dirty,
        };
      }
      return result;
    },
    getStats() {
      this.sending = true;

      const payload = Object.assign({}, this.form);
      Object.keys(payload).forEach(key => !payload[key] && delete payload[key]);

      const { dateFrom } = payload;
      const isoDateFrom = new Date(
        dateFrom.getTime() - dateFrom.getTimezoneOffset() * 60000,
      ).toISOString();

      /* Dikke hack: we willen de datums in UTC hebben, dus eem converteren */
      const { dateTo } = payload;
      const isoDateTo = new Date(
        dateTo.getTime() - dateTo.getTimezoneOffset() * 60000,
      ).toISOString();
      payload.dateFrom = isoDateFrom;
      payload.dateTo = isoDateTo;

      const client = applyConverters(this.$http);
      client.post(
        `${process.env.VUE_APP_BACKEND_URL}stats/query/`,
        payload,
      ).then((response) => {
        this.submissionError = false;
        this.submissionErrorDetails = null;
        this.result = JSON.parse(response.data);
        this.$emit('input', this.result);
      }).catch((error) => {
        // handle error
        this.submissionError = true;
        this.submissionErrorDetails = error;
        console.error(error);
      }).finally(() => {
        this.sending = false;
      });
    },
    validateStatsForm() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.getStats();
      }
    },

  },
  mounted() {
    this.form.dateFrom = this.getFirstOfMonth();
    this.form.dateTo = this.getToday();
  },
  computed: {
    dateTo() {
      return this.form.dateTo;
    },
  },
  watch: {
    dateTo(val) {
      // Set the selected time to be at the end of the selected day so we get the latest stats
      // from the backend including the ones that happened during the selected days
      if (val) {
        val.setHours(23, 59, 59, 999);
        this.form.dateTo = val;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .md-progress-bar {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
  }

  .md-title {
    font-size: 20px;
  }
</style>
